import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  Camera,
  Flame,
  Sparkles,
  TriangleAlert,
  UtensilsCrossed,
  X,
} from "lucide-react";
import { EXTRAS, IMAGES, MENU } from "@/data/content";
import { prefillEnquiry } from "@/lib/scroll";

const SectionHeading = ({ eyebrow, title, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease: "easeOut" }}
    className="mx-auto max-w-2xl text-center"
  >
    <p className="mb-4 flex items-center justify-center gap-4 text-xs uppercase tracking-[0.32em] text-[#D4AF37]">
      <span className="h-px w-10 bg-[#D4AF37]/50" />
      {eyebrow}
      <span className="h-px w-10 bg-[#D4AF37]/50" />
    </p>
    <h2
      data-testid={testId}
      className="font-display text-3xl tracking-tight text-[#1B3A6B] sm:text-4xl"
    >
      {title}
    </h2>
  </motion.div>
);

export { SectionHeading };

const EXTRA_ICONS = {
  candles: Flame,
  topper: Sparkles,
  knife: UtensilsCrossed,
};

const StyleLightbox = ({ style, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
    data-testid="style-lightbox"
    className="fixed inset-0 z-[80] flex items-center justify-center bg-[#1B3A6B]/85 p-6 backdrop-blur-sm"
  >
    <motion.div
      initial={{ scale: 0.94, y: 16 }}
      animate={{ scale: 1, y: 0 }}
      exit={{ scale: 0.96, y: 8 }}
      onClick={(e) => e.stopPropagation()}
      className="w-full max-w-2xl overflow-hidden rounded-sm border border-[#D4AF37]/30 bg-[#FDFBF7]"
    >
      <img
        src={IMAGES[style.image]}
        alt={`${style.name} reference`}
        data-testid="style-lightbox-image"
        className="max-h-[70vh] w-full object-cover"
      />
      <div className="flex items-center justify-between px-6 py-4">
        <div>
          <p className="font-label text-[10px] tracking-[0.3em] text-[#9A7B24]">
            STYLE REFERENCE
          </p>
          <p
            className="mt-1 font-display text-xl text-[#1B3A6B]"
            data-testid="style-lightbox-title"
          >
            {style.name}
          </p>
        </div>
        <button
          data-testid="style-lightbox-close"
          onClick={onClose}
          aria-label="Close"
          className="rounded-full border border-[#D4AF37]/40 p-2 text-[#8A6D1F] transition-colors hover:bg-[#D4AF37] hover:text-[#1B3A6B]"
        >
          <X size={16} />
        </button>
      </div>
    </motion.div>
  </motion.div>
);

export const PriceMenu = () => {
  const [active, setActive] = useState(MENU[0].id);
  const [style, setStyle] = useState(null);
  const item = MENU.find((m) => m.id === active);

  return (
    <section
      id="prices"
      data-testid="price-menu-section"
      className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32"
    >
      <SectionHeading
        eyebrow="Menu & Prices"
        title="The Cake Collections"
        testId="price-menu-heading"
      />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="mt-10 flex flex-wrap justify-center gap-3"
      >
        {MENU.map((m) => (
          <button
            key={m.id}
            data-testid={`menu-tab-${m.id}`}
            onClick={() => setActive(m.id)}
            className={`rounded-full border px-6 py-2.5 text-sm tracking-wide transition-all duration-300 ${
              active === m.id
                ? "border-[#D4AF37] bg-[#D4AF37] font-semibold text-[#1B3A6B]"
                : "border-[#D4AF37]/30 text-[#2F4A78] hover:border-[#D4AF37]/70 hover:text-[#8A6D1F]"
            }`}
          >
            {m.name}
          </button>
        ))}
      </motion.div>

      <AnimatePresence mode="wait">
        <motion.div
          key={item.id}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="mt-14 grid items-start gap-12 lg:grid-cols-2"
          data-testid={`menu-panel-${item.id}`}
        >
          <div className="relative">
            <div
              className="absolute -left-3 -top-3 h-full w-full rounded-sm border border-[#D4AF37]/40"
              aria-hidden
            />
            <img
              src={IMAGES[item.image]}
              alt={item.name}
              className="relative aspect-[4/3] w-full rounded-sm object-cover"
              data-testid={`menu-image-${item.id}`}
            />
            <div className="absolute -bottom-5 right-6 rounded-sm border border-[#D4AF37]/30 bg-[#FFFFFF]/95 px-6 py-3 backdrop-blur-xl">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#67718A]">
                Starting from
              </p>
              <p
                className="font-display text-2xl text-[#D4AF37]"
                data-testid={`menu-price-${item.id}`}
              >
                {item.fromPrice}
              </p>
            </div>
          </div>

          <div>
            <p className="font-label text-xs tracking-[0.3em] text-[#9A7B24]">
              {item.tagline.toUpperCase()}
            </p>
            <h3 className="mt-3 font-display text-2xl text-[#8A6D1F] sm:text-3xl">
              {item.name}
            </h3>
            <p className="mt-1 text-sm text-[#67718A]">{item.serves}</p>
            <p className="mt-5 text-base leading-relaxed text-[#2F4A78]">
              {item.description}
            </p>

            <div className="mt-8 divide-y divide-[#D4AF37]/15 border-y border-[#D4AF37]/15">
              {item.tiers.map((t) => (
                <div
                  key={t.name}
                  className="flex items-baseline justify-between gap-4 py-4"
                  data-testid={`menu-tier-${item.id}-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div>
                    <p className="font-display text-lg text-[#1B3A6B]">
                      {t.name}
                    </p>
                    <p className="text-xs uppercase tracking-[0.18em] text-[#67718A]">
                      {t.serves}
                    </p>
                  </div>
                  <p className="font-display text-xl text-[#D4AF37]">
                    {t.price}
                  </p>
                </div>
              ))}
            </div>

            {item.priceNote && (
              <p
                data-testid={`menu-price-note-${item.id}`}
                className="mt-4 text-xs italic leading-relaxed text-[#67718A]"
              >
                {item.priceNote}
              </p>
            )}

            {item.styles?.length > 0 && (
              <div className="mt-6" data-testid={`menu-styles-${item.id}`}>
                <p className="mb-2 text-[10px] uppercase tracking-[0.28em] text-[#9A7B24]">
                  Styles — tap to see a reference
                </p>
                <div className="flex flex-wrap gap-2">
                  {item.styles.map((st) => (
                    <button
                      key={st.name}
                      type="button"
                      data-testid={`style-chip-${st.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() =>
                        st.image && IMAGES[st.image] && setStyle(st)
                      }
                      className="flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-[#FBF7EE]/60 px-4 py-1.5 text-xs tracking-wide text-[#2F4A78] transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/15 hover:text-[#8A6D1F]"
                    >
                      {st.image && IMAGES[st.image] && (
                        <Camera size={12} className="text-[#9A7B24]" />
                      )}
                      {st.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 space-y-4">
              {[
                { label: "Flavours", items: item.flavors, key: "flavors" },
                { label: "Fillings", items: item.fillings, key: "fillings" },
              ]
                .filter((g) => g.items?.length)
                .map((g) => (
                  <div key={g.key} data-testid={`menu-${g.key}-${item.id}`}>
                    <p className="mb-2 text-[10px] uppercase tracking-[0.28em] text-[#9A7B24]">
                      {g.label}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {g.items.map((f) => (
                        <span
                          key={f}
                          className="rounded-full border border-[#D4AF37]/25 bg-[#FBF7EE]/60 px-4 py-1.5 text-xs tracking-wide text-[#2F4A78]"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
            </div>

            {item.allergens?.length > 0 && (
              <div
                className="mt-6 rounded-sm border border-[#1B3A6B]/10 bg-[#F7F2E8]/70 px-5 py-4"
                data-testid={`menu-allergens-${item.id}`}
              >
                <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#1B3A6B]">
                  <TriangleAlert size={13} className="text-[#9A7B24]" />{" "}
                  Allergens
                </p>
                <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#2F4A78]">
                  {item.allergens.map((a) => (
                    <li key={a} className="font-medium">
                      {a}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] leading-relaxed text-[#67718A]">
                  Made in a kitchen that handles gluten, eggs, milk, soya and
                  nuts — traces cannot be ruled out.
                </p>
              </div>
            )}

            <button
              data-testid={`menu-enquire-button-${item.id}`}
              onClick={() => prefillEnquiry({ event_type: item.eventType })}
              className="group mt-9 flex items-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3.5 text-sm font-semibold text-[#1B3A6B] transition-all duration-300 hover:bg-[#8A6D1F]"
            >
              Enquire for a Custom {item.eventType}
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>

      {EXTRAS.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6 }}
          data-testid="menu-extras"
          className="relative mt-16 overflow-hidden rounded-sm bg-[#1B3A6B] px-6 py-8 text-white sm:px-10"
        >
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full border border-[#D4AF37]/20"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -right-6 -top-6 h-56 w-56 rounded-full border border-[#D4AF37]/10"
            aria-hidden
          />
          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,260px)_1fr] lg:gap-14">
            <div>
              <p className="font-label text-[10px] tracking-[0.34em] text-[#D4AF37]">
                FINISHING TOUCHES
              </p>
              <p className="mt-3 font-display text-2xl leading-tight">
                Little extras,{" "}
                <span className="italic text-[#D4AF37]">added to any cake</span>
              </p>
              <p className="mt-3 text-xs leading-relaxed text-white/60">
                Just mention them in your enquiry and we'll add them to your
                quote.
              </p>
            </div>
            <ul className="grid gap-x-10 gap-y-5 sm:grid-cols-1 lg:grid-cols-3">
              {EXTRAS.map((x) => {
                const Icon = EXTRA_ICONS[x.id] || Sparkles;
                return (
                  <li
                    key={x.id}
                    data-testid={`menu-extra-${x.id}`}
                    className="flex items-start gap-4"
                  >
                    <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
                      <Icon size={16} className="text-[#D4AF37]" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm font-medium tracking-wide">
                          {x.name}
                        </p>
                        <p className="shrink-0 font-display text-lg text-[#D4AF37]">
                          {x.price}
                        </p>
                      </div>
                      {x.detail && (
                        <p className="mt-1 text-[11px] text-white/55">
                          {x.detail}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {style && (
          <StyleLightbox style={style} onClose={() => setStyle(null)} />
        )}
      </AnimatePresence>
    </section>
  );
};
